import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { Router } from '@angular/router';
import {
  IHowdenYesNoData,
  IResponse,
  LabelValueComponent,
  PageTitleComponent,
  YesNoService
} from '@howdeniberia/core-front';
import { EMPTY } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { TemporalState } from 'src/app/core/models/api/sane-project/arpa-model';
import { SaneProjectService } from 'src/app/core/services/api/projects/sane-project.service';
import { SharedService } from 'src/app/core/services/api/shared/shared.service';
import { toNumber } from 'src/app/shared/utils';
import { ProjectDraftMapper } from '../mappers/project-draft.mapper';
import { ProjectMapper } from '../mappers/project.mapper';
import { ProjectViewModel } from '../models/project.viewmodel';
import { allPresets } from '../sane-project-calculator/models/calculator-product.viewmodel';

type Industry = 'life' | 'accidents' | 'it' | 'dependence' | 'wholeLife' | 'deaths';
interface IInsurer { key: string; label: string; }

@Component({
  selector: 'howden-sane-project-resume',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatDividerModule,
    LabelValueComponent,
    PageTitleComponent
  ],
  templateUrl: './sane-project-resume.component.html',
  styleUrls: ['./sane-project-resume.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SaneProjectResumeComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly shared = inject(SharedService);
  private readonly saneSrv = inject(SaneProjectService);
  private readonly yesNoSrv = inject(YesNoService);
  private readonly fb = inject(FormBuilder);

  public project!: ProjectViewModel;

  readonly industries: Industry[] = ['life', 'accidents', 'it', 'dependence', 'wholeLife', 'deaths'];
  readonly presetsByIndustry = allPresets;
  readonly industryLabels: Record<Industry, string> = {
    life: 'VIDA',
    accidents: 'ACCIDENTES',
    it: 'IT',
    dependence: 'DEPENDENCIA',
    wholeLife: 'VIDA ENTERA',
    deaths: 'DECESOS'
  };

  ngOnInit(): void {
    const cached = this.shared.getModel();
    if (cached) {
      this.project = cached;
      return;
    }
    const draft = this.shared.loadDraft();
    if (!draft) {
      this.router.navigate(['sane-projects', 'edit']);
      return;
    }
    const vm = ProjectDraftMapper.fromDraft(draft, () => new ProjectViewModel(this.fb));
    this.project = vm;
    this.shared.setModel(vm);
  }

  private get paymentTypeId(): number | null {
    return this.project?.form.get('edit.projectPaymentType')?.value ?? null;
  }

  private get paymentTypeMaster(): any | undefined {
    const list = (this.project as any)?.edit?.paymentTypes as Array<any> | undefined;
    if (!list || !list.length) { return undefined; }
    return list.find(x => String(x?.id ?? '') === String(this.paymentTypeId ?? ''));
  }

  private static normalize(txt: unknown): string {
    return String(txt ?? '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  get paymentsPerYear(): number {
    const m = this.paymentTypeMaster;
    let numericFromField: number | null = null;
    if (Number.isFinite(m?.paymentsPerYear)) {
      numericFromField = Number(m.paymentsPerYear);
    } else if (Number.isFinite(m?.timesPerYear)) {
      numericFromField = Number(m.timesPerYear);
    }
    if (numericFromField && numericFromField > 0) { return numericFromField; }

    const desc = SaneProjectResumeComponent.normalize(m?.description);
    if (desc.includes('mensual') || desc.includes('monthly')) { return 12; }
    if (desc.includes('bimestral')) { return 6; }
    if (desc.includes('trimestral') || desc.includes('quarter')) { return 4; }
    if (desc.includes('cuatrimestral')) { return 3; }
    if (desc.includes('semestral') || desc.includes('semi')) { return 2; }
    if (desc.includes('anual') || desc.includes('annual') || desc.includes('unico') || desc.includes('único')) { return 1; }
    return 12;
  }

  get monthsPerPeriod(): number {
    return Math.max(1, Math.round(12 / this.paymentsPerYear));
  }

  get periodLabel(): string {
    switch (this.paymentsPerYear) {
      case 12: return 'MENSUAL';
      case 6: return 'BIMESTRAL';
      case 4: return 'TRIMESTRAL';
      case 3: return 'CUATRIMESTRAL';
      case 2: return 'SEMESTRAL';
      case 1: return 'ANUAL';
      default: return 'PERIODO';
    }
  }

  get showAnnualColumn(): boolean {
    return this.paymentsPerYear !== 1;
  }

  get insurersList(): IInsurer[] {
    const keys = new Set<string>();
    this.industries.forEach(ind =>
      Object.keys(this.presetsByIndustry[ind] || {}).forEach(k => keys.add(k))
    );

    return Array.from(keys).map(key => {
      const ind = this.industries.find(i => !!this.presetsByIndustry[i]?.[key])!;
      const preset = this.presetsByIndustry[ind][key]!;
      const rawName = preset.name;

      const core = rawName.toLowerCase().startsWith(ind.toLowerCase())
        ? rawName.slice(ind.length)
        : rawName;

      const spaced = core.replace(/([a-z])([A-Z])/g, '$1 $2').trim();
      const label = spaced.toUpperCase().replace(/\bPREBAL\b/g, 'PREVIS');

      return { key, label };
    });
  }

  get activeIndustries(): Industry[] {
    return this.industries.filter(ind => this.getIndustryPeriod(ind) > 0);
  }

  private getSection(ind: Industry): Record<string, any> {
    return ({
      life: this.project.calculatorLife,
      accidents: this.project.calculatorAccidents,
      it: this.project.calculatorIT,
      dependence: this.project.calculatorDependence,
      wholeLife: this.project.calculatorLifeWhole,
      deaths: this.project.calculatorDeaths
    } as any)[ind] || {};
  }

  getCellPeriod(insKey: string, ind: Industry): number {
    return toNumber(this.getSection(ind)[insKey]?.bonus) ?? 0;
  }

  getCellMonthly(insKey: string, ind: Industry): number {
    return this.getCellPeriod(insKey, ind) / this.monthsPerPeriod;
  }

  getRowPeriod(insKey: string): number {
    return this.industries.reduce((sum, ind) => sum + this.getCellPeriod(insKey, ind), 0);
  }

  getRowAnnual(insKey: string): number {
    return this.getRowPeriod(insKey) * this.paymentsPerYear;
  }

  getIndustryPeriod(ind: Industry): number {
    return this.insurersList.reduce((sum, ins) => sum + this.getCellPeriod(ins.key, ind), 0);
  }

  getIndustryAnnual(ind: Industry): number {
    return this.getIndustryPeriod(ind) * this.paymentsPerYear;
  }

  get totalPeriod(): number {
    return this.insurersList.reduce((sum, ins) => sum + this.getRowPeriod(ins.key), 0);
  }

  get totalAnnual(): number {
    return this.totalPeriod * this.paymentsPerYear;
  }

  trackByKey(_: number, item: IInsurer): string {
    return item.key;
  }

  private persistDraft(): void {
    this.shared.setModel(this.project);
    const draft = ProjectDraftMapper.toDraft(this.project);
    this.shared.saveDraft(draft);
  }

  onCancel(): void {
    this.persistDraft();
    this.router.navigate(['sane-projects', 'calculator']);
  }

  onSubmit(): void {
    const options: IHowdenYesNoData = {
      title: 'Confirmar',
      subTitle: '',
      question: '¿Deseas guardar el programa de Previsión Social Individual?',
      moreText: '',
      icon: 'help',
      okActionText: 'Sí',
      cancelActionText: 'No',
      disableClose: true,
      widthPixels: 500
    };

    this.yesNoSrv.open(options).pipe(
      tap(choice => {
        this.project.temporalState = choice?.resultData
          ? TemporalState.Active
          : TemporalState.Inactive;
        this.persistDraft();
      }),
      switchMap(() =>
        this.saneSrv.getPricing(ProjectMapper.mapForProject(this.project))
      ),
      switchMap((resp: IResponse<number>) => {
        if (!resp.result) {
          console.error('No se devolvió projectId al generar el programa de Previsión Social Individual');
          return EMPTY;
        }
        return this.saneSrv.generateReport(resp.result).pipe(
          map(blob => ({ blob, id: resp.result }))
        );
      })
    ).subscribe({
      next: ({ blob, id }) => {
        const pdfBlob = new Blob([blob], { type: 'application/pdf' });
        const fileURL = URL.createObjectURL(pdfBlob);
        window.open(fileURL, 'ProjectReportTab');
        const a = document.createElement('a');
        a.href = fileURL;
        a.download = `ProjectReport_${id}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(fileURL), 10000);
      },
      error: () => {
        console.error('Error generando el informe');
      }
    });
  }
}
